import {
  ActivityFeedDocument,
  CustomFieldsListDataDocument,
  GetApiKeysDocument,
  GetAutomationRunsDocument,
  GetAutomationsDocument,
  GetIssueSourcesDocument,
  GetIssueTypesDocument,
  GetReasonsDocument,
  GetTrashDocument,
  GetWebhooksDocument,
  GetWorkspacesDocument,
  IssueNotesDataDocument,
  IssuesDocument,
  MembersGroupDataDocument,
  MembersListDataDocument,
  NotificationsViewDataDocument,
  StagesByWorkflowDocument,
  TeamsListDataDocument,
  WidgetsDocument,
  WorkflowsListDataDocument,
} from "#app/graphql/generated/graphql";
import {
  ACTIVITY_LIST,
  API_KEY_LIST,
  AUTOMATION_LIST,
  AUTOMATION_RUN_LIST,
  CUSTOM_FIELD_LIST,
  ISSUE_LIST,
  ISSUE_SOURCE_LIST,
  ISSUE_TYPE_LIST,
  MEMBER_LIST,
  NOTE_LIST,
  NOTIFICATION_LIST,
  REASON_LIST,
  ROLE_LIST,
  STAGE_LIST,
  TEAM_LIST,
  TRASH_LIST,
  WEBHOOK_LIST,
  WIDGET_LIST,
  WORKFLOW_LIST,
  WORKSPACE_LIST,
} from "#app/lib/filters/list-configs";
import { defineArrayResource, defineResource } from "./types";
import type {
  ActivityRow,
  ApiKeyRow,
  AutomationRow,
  AutomationRunRow,
  CustomFieldRow,
  IssueRow,
  IssueSourceRow,
  IssueTypeRow,
  MemberRow,
  NoteRow,
  NotificationRow,
  ReasonRow,
  RoleRow,
  StageRow,
  TeamRow,
  TrashRow,
  WebhookRow,
  WidgetRow,
  WorkflowRow,
  WorkspaceRow,
} from "./rows";

// ---------------------------------------------------------------------------
// The resource registry — one entry per listed noun
// ---------------------------------------------------------------------------
//
// This file, and only this file, names the GraphQL wiring for an entity list.
// A new noun (accounts, contacts) is one entry here plus a column set beside
// its screen. Nothing else changes.
//
// Every field is written out. Nothing is derived from a type name, because the
// API's conventions break too often for that to be safe — see the `document`
// and `connection` of TRASH, WIDGETS and AUTOMATION_RUNS for three shapes no
// generic layer would guess.
//
// `rowId` names the field the Apollo cache keys on (`lib/data/apollo-client.ts`
// typePolicies). The `<noun>Id` twin is a CLOSED legacy set: it exists only on
// entities that predate the REST swap. Automation, AutomationRun, Widget and
// Media have no twin and key on `id`; Notification's twin is `inboxId`. A NEW
// noun keys on `id` — do not add a twin.

export const ACTIVITIES = defineResource<ActivityRow>({
  key: "activities",
  label: { one: "activity", many: "activities" },
  query: ACTIVITY_LIST,
  document: ActivityFeedDocument,
  connection: (data) => data?.activities,
  rowId: (activity) => activity.activityId,
});

export const API_KEYS = defineResource<ApiKeyRow>({
  key: "apiKeys",
  label: { one: "API key", many: "API keys" },
  query: API_KEY_LIST,
  document: GetApiKeysDocument,
  connection: (data) => data?.apiKeys,
  rowId: (apiKey) => apiKey.apiKeyId,
});

export const AUTOMATIONS = defineResource<AutomationRow>({
  key: "automations",
  label: { one: "automation", many: "automations" },
  query: AUTOMATION_LIST,
  document: GetAutomationsDocument,
  connection: (data) => data?.automations,
  // AutomationConnection has no pageInfo, and its edge type is
  // `AutomationListEdge` because `AutomationEdge` is the graph-edge type on
  // `Automation.edges`.
  shape: { noPageInfo: true },
  rowId: (automation) => automation.id,
});

export const AUTOMATION_RUNS = defineResource<AutomationRunRow>({
  key: "automationRuns",
  label: { one: "run", many: "runs" },
  query: AUTOMATION_RUN_LIST,
  // The runs a screen pages are the NESTED connection on one automation.
  // `Query.automationRuns` is a plain array with no filter surface.
  document: GetAutomationRunsDocument,
  connection: (data) => data?.automation?.runs,
  rowId: (run) => run.id,
});

export const CUSTOM_FIELDS = defineResource<CustomFieldRow>({
  key: "customFields",
  label: { one: "field", many: "fields" },
  query: CUSTOM_FIELD_LIST,
  document: CustomFieldsListDataDocument,
  connection: (data) => data?.customFields,
  rowId: (field) => field.customFieldId,
});

export const ISSUES = defineResource<IssueRow>({
  key: "issues",
  label: { one: "issue", many: "issues" },
  query: ISSUE_LIST,
  document: IssuesDocument,
  connection: (data) => data?.issues,
  rowId: (issue) => issue.issueId,
});

export const ISSUE_SOURCES = defineResource<IssueSourceRow>({
  key: "issueSources",
  label: { one: "source", many: "sources" },
  query: ISSUE_SOURCE_LIST,
  document: GetIssueSourcesDocument,
  connection: (data) => data?.issueSources,
  shape: { noPageInfo: true },
  rowId: (source) => source.issueSourceId,
});

export const ISSUE_TYPES = defineResource<IssueTypeRow>({
  key: "issueTypes",
  label: { one: "type", many: "types" },
  query: ISSUE_TYPE_LIST,
  document: GetIssueTypesDocument,
  connection: (data) => data?.issueTypes,
  rowId: (issueType) => issueType.issueTypeId,
});

export const MEMBERS = defineResource<MemberRow>({
  key: "members",
  label: { one: "member", many: "members" },
  query: MEMBER_LIST,
  document: MembersListDataDocument,
  // The list operation carries `roles` and `teams` alongside the members
  // themselves; the grouped operation is the same selection without them.
  groupDocument: MembersGroupDataDocument,
  connection: (data) => data?.users,
  rowId: (member) => member.userId,
});

export const NOTES = defineResource<NoteRow>({
  key: "notes",
  label: { one: "note", many: "notes" },
  query: NOTE_LIST,
  document: IssueNotesDataDocument,
  connection: (data) => data?.notes,
  rowId: (note) => note.noteId,
});

export const NOTIFICATIONS = defineResource<NotificationRow>({
  key: "notifications",
  label: { one: "notification", many: "notifications" },
  query: NOTIFICATION_LIST,
  document: NotificationsViewDataDocument,
  connection: (data) => data?.notifications,
  shape: { extra: ["unreadCount"] },
  // The twin is `inboxId`, NOT `notificationId`.
  rowId: (notification) => notification.inboxId,
});

export const REASONS = defineResource<ReasonRow>({
  key: "reasons",
  label: { one: "reason", many: "reasons" },
  query: REASON_LIST,
  document: GetReasonsDocument,
  connection: (data) => data?.reasons,
  shape: { noPageInfo: true },
  rowId: (reason) => reason.reasonId,
});

export const ROLES = defineResource<RoleRow>({
  key: "roles",
  label: { one: "role", many: "roles" },
  query: ROLE_LIST,
  // There is no standalone roles operation. The workspace role list is a
  // sibling connection inside MembersListData, which every screen that needs
  // roles already runs. Pointing at that document states the fact instead of
  // adding an operation nothing calls.
  document: MembersListDataDocument,
  connection: (data) => data?.roles,
  shape: { noPageInfo: true },
  rowId: (role) => role.roleId,
});

export const STAGES = defineResource<StageRow>({
  key: "stages",
  label: { one: "stage", many: "stages" },
  query: STAGE_LIST,
  // Stages are always read for ONE workflow — the document requires
  // `workflowId`. There is no workspace-wide stage screen.
  document: StagesByWorkflowDocument,
  connection: (data) => data?.stages,
  rowId: (stage) => stage.stageId,
});

export const TEAMS = defineResource<TeamRow>({
  key: "teams",
  label: { one: "team", many: "teams" },
  query: TEAM_LIST,
  document: TeamsListDataDocument,
  connection: (data) => data?.teams,
  rowId: (team) => team.teamId,
});

export const TRASH = defineResource<TrashRow>({
  key: "trash",
  label: { one: "item", many: "items" },
  query: TRASH_LIST,
  document: GetTrashDocument,
  // TrashConnection's nodes are `Issue`, so the row is an issue and its id is
  // the issue twin. `TrashEdge.trashedAt` duplicates `Issue.trashedAt`, which
  // the row already carries.
  connection: (data) => data?.trash,
  shape: { extra: ["truncated"] },
  rowId: (item) => item.issueId,
});

export const WEBHOOKS = defineResource<WebhookRow>({
  key: "webhooks",
  label: { one: "webhook", many: "webhooks" },
  query: WEBHOOK_LIST,
  document: GetWebhooksDocument,
  connection: (data) => data?.webhooks,
  shape: { noPageInfo: true },
  rowId: (webhook) => webhook.webhookId,
});

export const WIDGETS = defineArrayResource<WidgetRow>({
  key: "widgets",
  label: { one: "widget", many: "widgets" },
  query: WIDGET_LIST,
  // `widgets(view: String!)` returns a PLAIN ARRAY and requires its argument.
  document: WidgetsDocument,
  rows: (data) => data?.widgets ?? [],
  rowId: (widget) => widget.id,
});

export const WORKFLOWS = defineResource<WorkflowRow>({
  key: "workflows",
  label: { one: "workflow", many: "workflows" },
  query: WORKFLOW_LIST,
  document: WorkflowsListDataDocument,
  connection: (data) => data?.workflows,
  rowId: (workflow) => workflow.workflowId,
});

export const WORKSPACES = defineResource<WorkspaceRow>({
  key: "workspaces",
  label: { one: "workspace", many: "workspaces" },
  query: WORKSPACE_LIST,
  // `workspaces` takes no arguments at all.
  document: GetWorkspacesDocument,
  connection: (data) => data?.workspaces,
  shape: { noPageInfo: true },
  rowId: (workspace) => workspace.workspaceId,
});

/**
 * Every registered noun, by key.
 *
 * `Media` has no entry because it has no root `Query` field to point one at.
 * It reaches the client two ways: nested (`Note.media`) and over REST
 * (`apps/api/core/src/media/media.controller.ts`). If a `media` list query
 * ever lands, it gets an entry like any other noun.
 */
export const RESOURCES = {
  activities: ACTIVITIES,
  apiKeys: API_KEYS,
  automations: AUTOMATIONS,
  automationRuns: AUTOMATION_RUNS,
  customFields: CUSTOM_FIELDS,
  issues: ISSUES,
  issueSources: ISSUE_SOURCES,
  issueTypes: ISSUE_TYPES,
  members: MEMBERS,
  notes: NOTES,
  notifications: NOTIFICATIONS,
  reasons: REASONS,
  roles: ROLES,
  stages: STAGES,
  teams: TEAMS,
  trash: TRASH,
  webhooks: WEBHOOKS,
  widgets: WIDGETS,
  workflows: WORKFLOWS,
  workspaces: WORKSPACES,
} as const;

export type ResourceKey = keyof typeof RESOURCES;
